import type { IndicatorBandingConfig } from '@risksmart-app/domain/src/types/indicator-banding-config';
import { indicatorBandingConfigSchema } from '@risksmart-app/domain/src/types/indicator-banding-config';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTRPC } from 'src/utils/trpc';

export type IndicatorBandingConfigById = Map<
  string,
  { config: IndicatorBandingConfig; indicatorId: string; version: number }
>;

/**
 * Must match the `.max(500)` on BOTH the tRPC input
 * (routers/frontend/indicator-banding-config.router.ts) and the data-layer
 * request schema. Zod rejects the WHOLE request over the cap, so an
 * unbatched call past it fails every id rather than degrading — leaving the
 * register, results tab and chart showing "Not set" everywhere.
 */
const BATCH_SIZE = 500;

/**
 * Batch-fetches SPECIFIC banding config versions by their own `Id` — not
 * necessarily the latest version for their indicator. For frozen per-result
 * conformance display (CUS-188): a result's `BandingConfigId` pins the
 * exact version active when it was logged, which may since have been
 * superseded by a newer version. Looking that pinned version up (rather
 * than the indicator's current latest) is what makes the display frozen —
 * editing bands later never changes what an already-logged result shows.
 *
 * Callers collect the distinct `BandingConfigId`s across whatever set of
 * results they're rendering (e.g. one per visible register row) and pass
 * them here in one batch; this splits them across as many requests as the
 * server cap needs and merges the responses into one map.
 */
export const useGetIndicatorBandingConfigsByIds = (
  ids: string[],
  options?: { isSkipped?: boolean }
) => {
  const trpc = useTRPC();
  const enabled = ids.length > 0 && !options?.isSkipped;

  const batches = useMemo(() => {
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      chunks.push(ids.slice(i, i + BATCH_SIZE));
    }

    return chunks;
  }, [ids]);

  // `combine` rather than a `useMemo` over `results`: useQueries gives a fresh
  // array identity every render, so a memo keyed on it either recomputes each
  // render or needs a hand-rolled dependency the exhaustive-deps rule cannot
  // check. TanStack memoizes `combine` against the underlying query results.
  return useQueries({
    queries: batches.map((batch) => ({
      ...trpc.frontend.indicatorBandingConfig.byIds.queryOptions({
        ids: batch,
      }),
      enabled,
    })),
    combine: (results) => {
      const configById: IndicatorBandingConfigById = new Map();

      for (const result of results) {
        for (const record of result.data ?? []) {
          if (!record.Config) {
            continue;
          }
          configById.set(record.Id, {
            config: indicatorBandingConfigSchema.parse(record.Config),
            indicatorId: record.IndicatorId,
            version: record.Version,
          });
        }
      }

      // `isPending` rather than `isLoading`: in TanStack Query v5 an
      // enabled-but-not-yet-fetching query reports `isLoading === false`, so
      // keying off it would flip `loading` false while later batches are
      // still unresolved — handing callers a partial map that renders
      // "Not set" for every id in the outstanding batches. Same reasoning as
      // useManageableApprovalResourceTypes.
      return {
        configById,
        loading: enabled && results.some((result) => result.isPending),
      };
    },
  });
};
